// Mobile landscape images
for (const image of document.querySelectorAll('.single-image__mobile-landscape picture')) {
	let prevScroll = 0;

	const imageElem = image.querySelector('img');
	const container = image.closest('.single-image__mobile-landscape');
	const parent = image.closest('.single-image__parent-container');

	function easeInOutQuad(x) {
		return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
	}

	const animate = () => {
		if (window.innerWidth < 992 && container.offsetTop != parent.offsetTop && container.getBoundingClientRect().top > 0) {
			let scrollAmount = (container.offsetTop - parent.offsetTop) / (parent.clientHeight - container.clientHeight);
		
			scrollAmount -= 0.05;
			scrollAmount = scrollAmount / 0.92;
			scrollAmount = Math.max(0, Math.min(scrollAmount, 1));
			const transformAmount = (imageElem.scrollWidth - image.clientWidth) / imageElem.scrollWidth;
			const transformVal = scrollAmount * transformAmount;

			if (scrollAmount != prevScroll) {
				imageElem.style.transform = `translateX(-${transformVal * 100}%)`;
				prevScroll = scrollAmount;
			}
		}

		requestAnimationFrame(animate);
	}

	animate();
}


class SanityCards extends HTMLElement {
	constructor() {
		super();
		console.log('SanityCards element created');

		for (const page of this.querySelectorAll('.sanity-cards__pagination button')) {
			page.addEventListener('click', async e => {
				e.preventDefault();
				this.classList.add('sanity-cards--loading');

				const url = page.getAttribute('href');
				const resp = await fetch(url);
				const text = await resp.text();

				this.outerHTML = text;
			});
		}
	}
}

customElements.define('sanity-cards', SanityCards);

// all_products can only resolve 20 handles per page, so product blocks and galleries fetch their
// products with a product view instead. Resolves to null for a deleted, unpublished or unsold product
const productViews = new Map();

function fetchProductView(handle, view) {
	const url = `${window.Shopify?.routes?.root ?? '/'}products/${encodeURIComponent(handle)}?view=${view}`;

	if (!productViews.has(url)) {
		productViews.set(url, fetch(url).then(resp => resp.ok ? resp.text() : null).catch(() => null));
	}

	return productViews.get(url);
}

function whenNearViewport(elem, callback) {
	const observer = new IntersectionObserver(entries => {
		if (entries.some(e => e.isIntersecting)) {
			observer.disconnect();
			callback();
		}
	}, { rootMargin: '100% 0px' });

	observer.observe(elem);
}

// Product block: the first product is server-rendered, the rest are fetched as the block nears
// the viewport
class SanityProductBlock extends HTMLElement {

	connectedCallback() {
		this.thumbs = this.querySelector('.product-block__thumbs');
		this.leadSlide = this.querySelector('[data-lead-slide]');

		this.thumbs?.addEventListener('click', e => {
			const thumb = e.target.closest('.product-block__thumb');
			if (thumb) this.select(thumb);
		});

		// Single product blocks: keep the thumbnails in step with a swiped gallery
		this.querySelector('.product-card-grid-slider[events]')?.addEventListener('cardchange', e => {
			this.highlight(this.thumbs.children[e.detail.index]);
		});

		if (this.querySelector('.multi-product-card[data-handle]')) whenNearViewport(this, () => this.load());
	}

	async load() {
		const cards = [...this.querySelectorAll('.multi-product-card[data-handle]')];

		await Promise.all(cards.map(async card => {
			const { product, handle } = card.dataset;
			const html = await fetchProductView(handle, 'sanity-block-item');
			const thumb = this.querySelector(`.product-block__thumb[data-product="${product}"]`);
			const details = this.querySelector(`.product-block__title-price-container[data-product="${product}"]`);

			// Deleted, unpublished or not sold in this market
			if (!html) {
				if (thumb?.getAttribute('aria-pressed') === 'true') this.select(this.thumbs.firstElementChild);
				for (const elem of [card, thumb, details]) elem?.remove();
				return;
			}

			const template = document.createElement('template');
			template.innerHTML = html;
			const part = name => template.content.querySelector(`[data-part="${name}"]`);

			if (this.leadSlide) part('card').querySelector('.product-card-grid-slider').prepend(this.leadSlide.cloneNode(true));

			card.replaceChildren(...part('card').childNodes);
			card.removeAttribute('data-handle');
			if (card.classList.contains('active')) this.showSlide(card);

			if (thumb) {
				thumb.replaceChildren(...part('thumbnail').childNodes);
				thumb.setAttribute('aria-label', part('thumbnail').dataset.title);
			}

			details?.replaceChildren(...part('details').childNodes);
		}));
	}

	select(thumb) {
		this.highlight(thumb);

		// Single product blocks: thumbnails are the product's images
		if ('slide' in thumb.dataset) {
			this.querySelector('.product-card-grid-slider').scrollToIndex(Number(thumb.dataset.slide));
			return;
		}

		// Multi product blocks: thumbnails are the products, or the lead image (no data-product)
		const { product } = thumb.dataset;

		for (const elem of this.querySelectorAll('.product-block__title-price-container')) {
			elem.classList.toggle('active', elem.dataset.product === product);
		}

		if (product) {
			for (const card of this.querySelectorAll('.multi-product-card')) {
				card.classList.toggle('active', card.dataset.product === product);
			}
		}

		const card = this.querySelector('.multi-product-card.active');
		card.classList.toggle('first-slide-hide-quick-addtocart', !product);
		this.showSlide(card, product ? undefined : 0);
	}

	// Products open on their own first image, after the lead image
	showSlide(card, index = this.leadSlide ? 1 : 0) {
		requestAnimationFrame(() => card.querySelector('.product-card-grid-slider')?.scrollToIndex(index, { smooth: false }));
	}

	highlight(thumb) {
		if (!thumb) return;

		for (const other of this.thumbs.children) other.setAttribute('aria-pressed', other === thumb);
		this.thumbs.scrollToIndex([...this.thumbs.children].indexOf(thumb));
	}
}

customElements.define('sanity-product-block', SanityProductBlock);

// Product gallery: every card is a placeholder until the gallery nears the viewport
class SanityProductGallery extends HTMLElement {
	connectedCallback() {
		whenNearViewport(this, () => this.load());
	}

	async load() {
		await Promise.all([...this.querySelectorAll('.card--placeholder[data-handle]')].map(async placeholder => {
			const html = await fetchProductView(placeholder.dataset.handle, 'sanity-gallery-item');
			const template = document.createElement('template');
			template.innerHTML = html ?? '';

			// A missing product drops out rather than leaving a blank card
			placeholder.replaceWith(template.content);
		}));

		if (!this.querySelector('.product-gallery__items > *')) this.remove();
	}
}

customElements.define('sanity-product-gallery', SanityProductGallery);
